import React, { useEffect, useState } from 'react';
import useLocationSearch from '../../hooks/useLocationSearch';
import { Alert } from 'react-bootstrap';

export default function SigningThankYouPage({ location }) {
    const searchParams = useLocationSearch({ location });
    const [redirectUrl, setRedirectUrl] = useState('');
    const [showFallback, setShowFallback] = useState(false);
    const [hasReturnUrl, setHasReturnUrl] = useState(false);

    // Helper function for reliable cross-origin redirect using target="_top"
    const attemptRedirect = (url) => {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_top';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);

        link.click();

        // Cleanup after brief delay
        setTimeout(() => {
            try {
                if (document.body.contains(link)) {
                    document.body.removeChild(link);
                }
            } catch (e) {
                // Page may already be navigating away, but log unexpected errors for debugging
                console.debug('Cleanup of redirect link failed in attemptRedirect:', e);
            }
        }, 100);
    };

    useEffect(() => {
        if (searchParams && searchParams.event === 'signing_complete' && searchParams.returnUrl) {
            // Vendor redirect scenario - we have a returnUrl
            setHasReturnUrl(true);

            const returnUrl = searchParams.returnUrl;

            // Validate that returnUrl is an absolute URL
            let url;
            try {
                url = new URL(returnUrl);

                // Ensure the URL has a valid protocol (http or https)
                if (!['http:', 'https:'].includes(url.protocol)) {
                    console.error('Invalid returnUrl protocol:', url.protocol);
                    setShowFallback(true);
                    return;
                }
            } catch (error) {
                console.error('Invalid returnUrl - must be an absolute URL:', error);
                setShowFallback(true);
                return;
            }

            // Preserve all DocuSign parameters except returnUrl
            const docusignParams = { ...searchParams };
            delete docusignParams.returnUrl;

            // Append DocuSign parameters to the return URL
            Object.keys(docusignParams).forEach(key => {
                url.searchParams.append(key, docusignParams[key]);
            });

            const finalUrl = url.toString();
            setRedirectUrl(finalUrl);

            // Attempt immediate redirect using target="_top" link (most reliable for cross-origin)
            attemptRedirect(finalUrl);

            // Show fallback button after 2 seconds if redirect didn't work
            const fallbackTimeout = setTimeout(() => {
                setShowFallback(true);
            }, 2000);

            return () => {
                clearTimeout(fallbackTimeout);
            };
        }
    }, [searchParams]);

    // Handle manual redirect
    const handleManualRedirect = () => {
        if (redirectUrl) {
            attemptRedirect(redirectUrl);
        }
    };

    if (searchParams && searchParams.event === 'ttl_expired') {
        return <Alert>DocuSign link was expired</Alert>;
    }

    if (searchParams && searchParams.event === 'signing_complete') {
        return (
            <div className="content">
                <h2>Thank you</h2>
                <div style={{ marginTop: '2rem' }}>
                    <p style={{ fontWeight: 'bold' }}>What&apos;s next?</p>
                    <p style={{ width: '50%' }}>
                        Sit back and relax. Our dedicated team will review your documents, so you can get back to growing your business.
                    </p>
                </div>

                {/* Show fallback UI only if redirect didn't work (after 2 seconds) */}
                {hasReturnUrl && showFallback && redirectUrl && (
                    <div style={{ 
                        marginTop: '2rem', 
                        padding: '1.5rem', 
                        backgroundColor: '#fff3cd', 
                        borderRadius: '8px', 
                        border: '1px solid #ffc107' 
                    }}>
                        <p style={{ marginBottom: '1rem', fontSize: '1rem', color: '#856404', fontWeight: 'bold' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', color: '#ffc107' }}></i>
                            Automatic redirect did not complete
                        </p>
                        
                        <p style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#856404' }}>
                            Please click the button below to continue back to the vendor:
                        </p>
                        
                        <button
                            onClick={handleManualRedirect}
                            className="btn btn-primary"
                            style={{
                                padding: '10px 20px',
                                fontSize: '1rem',
                                fontWeight: '500',
                                backgroundColor: '#007bff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                        >
                            <i className="fas fa-external-link-alt" style={{ marginRight: '8px' }}></i>
                            Click Here to Continue
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return <div></div>;
}
